import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calculator, Zap, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TPSLCalculatorService, TPSLCalculationResult } from '@/services/TPSLCalculatorService';
import PositionMappingModal from './PositionMappingModal';
import { useCustomPositionMappings } from '@/hooks/useCustomPositionMappings';
import { toast } from '@/hooks/use-toast';

interface PercentageCalculatorPanelProps {
  onCalculationComplete?: (results: Map<string, TPSLCalculationResult>) => void;
  filenames?: string[];
  onAutoApplyMinPositionSize?: () => void;
}

export const PercentageCalculatorPanel: React.FC<PercentageCalculatorPanelProps> = ({
  onCalculationComplete,
  filenames = [],
  onAutoApplyMinPositionSize
}) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [autoMode, setAutoMode] = useState(true);
  const [manualInputs, setManualInputs] = useState({
    symbol: '',
    positionSize: 0.01,
    tpPercent: 2.0,
    slPercent: 2.0,
    currentPrice: ''
  });
  const [results, setResults] = useState<Map<string, TPSLCalculationResult>>(new Map());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [positionSizeInfo, setPositionSizeInfo] = useState<Map<string, { min: number; max: number; count: number }>>(new Map());
  const { getMapping, hasMapping } = useCustomPositionMappings();

  const handleBatchCalculation = async () => {
    if (filenames.length === 0) {
      toast({
        title: "No files selected",
        description: "Please upload CSV files first.",
        variant: "destructive",
      });
      return;
    }

    setIsCalculating(true);
    setErrors(new Map());
    setPositionSizeInfo(new Map());
    
    try {
      // First, gather position size information from all files
      const positionInfoMap = new Map<string, { min: number; max: number; count: number }>();
      
      for (const filename of filenames) {
        try {
          const positionData = await TPSLCalculatorService.extractPositionSizesFromCsv(filename);
          if (positionData.hasPositionData && positionData.allPositionSizes.length > 0) {
            positionInfoMap.set(filename, {
              min: Math.min(...positionData.allPositionSizes),
              max: Math.max(...positionData.allPositionSizes),
              count: positionData.allPositionSizes.length
            });
          }
        } catch (error) {
          console.warn(`Could not extract position data from ${filename}:`, error);
        }
      }
      
      setPositionSizeInfo(positionInfoMap);
      
      const batchResults = await TPSLCalculatorService.batchCalculateFromFilenames(filenames);
      
      const successResults = new Map<string, TPSLCalculationResult>();
      const errorResults = new Map<string, string>();
      
      batchResults.forEach((result, filename) => {
        if (result instanceof Error) {
          errorResults.set(filename, result.message);
        } else {
          successResults.set(filename, result);
        }
      });
      
      setResults(successResults);
      setErrors(errorResults);
      
      if (successResults.size > 0) {
        toast({
          title: "Calculation completed",
          description: `Processed ${successResults.size} files successfully.`,
        });
        onCalculationComplete?.(successResults);
        
        // Auto-apply min position sizes after successful calculation
        if (onAutoApplyMinPositionSize) {
          setTimeout(() => {
            onAutoApplyMinPositionSize();
          }, 1000); // Small delay to let the TP/SL results process first
        }
      }
      
      if (errorResults.size > 0) {
        toast({
          title: "Some calculations failed",
          description: `${errorResults.size} files could not be processed.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Calculation failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleManualCalculation = async () => {
    if (!manualInputs.symbol || manualInputs.positionSize <= 0) {
      toast({
        title: "Invalid inputs",
        description: "Please fill in all required fields with valid values.",
        variant: "destructive",
      });
      return;
    }

    setIsCalculating(true);
    
    try {
      const result = await TPSLCalculatorService.calculateTPSLFromPercentage({
        symbol: manualInputs.symbol.toUpperCase(),
        positionSize: manualInputs.positionSize,
        tpPercent: manualInputs.tpPercent,
        slPercent: manualInputs.slPercent,
        currentPrice: manualInputs.currentPrice ? parseFloat(manualInputs.currentPrice) : undefined
      });
      
      const newResults = new Map([[`manual_${Date.now()}`, result]]);
      setResults(newResults);
      
      toast({
        title: "Calculation completed",
        description: `TP: $${result.tpUSD.toFixed(2)}, SL: $${result.slUSD.toFixed(2)}`,
      });
      
      onCalculationComplete?.(newResults);
    } catch (error) {
      toast({
        title: "Calculation failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const formatPrice = (price: number, symbol?: string) => {
    if (!symbol) {
      if (price >= 1) return price.toFixed(2);
      if (price >= 0.01) return price.toFixed(4);
      return price.toFixed(8);
    }

    const upperSymbol = symbol.toUpperCase();
    
    // Crypto pairs - show more decimals
    if (upperSymbol.includes('USD') && (upperSymbol.includes('BTC') || upperSymbol.includes('ETH'))) {
      return price.toFixed(2);
    }
    if (upperSymbol.includes('USDT') || upperSymbol.includes('USDC')) {
      return price.toFixed(6);
    }
    
    // JPY pairs - typically 3 decimal places
    if (upperSymbol.includes('JPY')) {
      return price.toFixed(3);
    }
    
    // Gold - 2 decimal places
    if (upperSymbol.includes('XAU') || upperSymbol.includes('GOLD')) {
      return price.toFixed(2);
    }
    
    // Forex majors - 5 decimal places
    return price.toFixed(5);
  };

  const formatUSD = (amount: number) => {
    if (amount === 0) return '$0.00';
    if (amount < 0.01) return `$${amount.toFixed(4)}`;
    return `$${amount.toFixed(2)}`;
  };

  const formatLotSize = (size: number) => {
    if (size >= 1) return size.toFixed(2);
    if (size >= 0.01) return size.toFixed(3);
    return size.toFixed(6);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          TP/SL Percentage Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mode Toggle and Position Mapping */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Label htmlFor="auto-mode">Auto-parse from filenames</Label>
            <Switch
              id="auto-mode"
              checked={autoMode}
              onCheckedChange={setAutoMode}
            />
          </div>
          <PositionMappingModal 
            open={false} 
            onOpenChange={() => {}} 
            onMappingUpdate={() => {
              // Force re-render to pick up new mappings
              window.location.reload();
            }} 
          />
        </div>

        {autoMode ? (
          /* Auto Mode */
          <div className="space-y-4">
            <Alert>
              <Zap className="h-4 w-4" />
              <AlertDescription>
                Auto-parse mode will extract symbol and TP/SL percentages from your filename patterns.
                <br />
                Supported formats: "SYMBOL - TP% - TIMEFRAME" or "SYMBOL - TP%_SL% - TIMEFRAME"
              </AlertDescription>
            </Alert>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {filenames.length} files loaded
              </Badge>
              {filenames.length > 0 && (
                <Button
                  onClick={handleBatchCalculation}
                  disabled={isCalculating}
                  size="sm"
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Calculate All
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Preview of parsed filenames */}
            {filenames.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1">
                <p className="text-sm font-medium">File Preview:</p>
                {filenames.slice(0, 5).map((filename, index) => {
                  const parsed = TPSLCalculatorService.parseFilename(filename);
                  const posInfo = positionSizeInfo.get(filename);
                  return (
                    <div key={index} className="text-xs p-2 bg-muted rounded text-muted-foreground">
                      <span className="font-medium">{filename}</span>
                      {parsed.isValid ? (
                        <div className="ml-2 space-y-1">
                          <span className="text-primary">
                            → {parsed.symbol} (TP: {parsed.tpPercent}%, SL: {parsed.slPercent}%)
                          </span>
                          {posInfo && (
                            <div className="text-blue-600">
                              → Position sizes: {formatLotSize(posInfo.min)} - {formatLotSize(posInfo.max)} ({posInfo.count} trades)
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="ml-2 text-destructive">
                          → Parse failed: {parsed.error}
                        </span>
                      )}
                    </div>
                  );
                })}
                {filenames.length > 5 && (
                  <p className="text-xs text-muted-foreground">
                    ... and {filenames.length - 5} more files
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Manual Mode */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="symbol">Symbol</Label>
                <Input
                  id="symbol"
                  placeholder="EURUSD, BTCUSD, etc."
                  value={manualInputs.symbol}
                  onChange={(e) => {
                    const symbol = e.target.value.toUpperCase();
                    setManualInputs(prev => ({ ...prev, symbol }));
                    
                    // Auto-set position size if custom mapping exists
                    if (symbol && hasMapping(symbol)) {
                      const mapping = getMapping(symbol);
                      if (mapping) {
                        // Calculate TV position to get ~0.01 cTrader lots
                        const targetCTraderLots = 0.01;
                        const tvPosition = targetCTraderLots / mapping.tvToCtraderRatio;
                        setManualInputs(prev => ({ ...prev, positionSize: tvPosition }));
                      }
                    }
                  }}
                />
                {manualInputs.symbol && hasMapping(manualInputs.symbol) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Custom mapping: 1 TV = {getMapping(manualInputs.symbol)?.tvToCtraderRatio} cT lots
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="position-size">Position Size (TV)</Label>
                <Input
                  id="position-size"
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={manualInputs.positionSize}
                  onChange={(e) => setManualInputs(prev => ({ ...prev, positionSize: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label htmlFor="tp-percent">Take Profit (%)</Label>
                <Input
                  id="tp-percent"
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={manualInputs.tpPercent}
                  onChange={(e) => setManualInputs(prev => ({ ...prev, tpPercent: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label htmlFor="sl-percent">Stop Loss (%)</Label>
                <Input
                  id="sl-percent"
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={manualInputs.slPercent}
                  onChange={(e) => setManualInputs(prev => ({ ...prev, slPercent: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="current-price">Current Price (optional)</Label>
                <Input
                  id="current-price"
                  type="number"
                  step="0.00001"
                  placeholder="Leave empty to fetch live price"
                  value={manualInputs.currentPrice}
                  onChange={(e) => setManualInputs(prev => ({ ...prev, currentPrice: e.target.value }))}
                />
              </div>
            </div>

            <Button
              onClick={handleManualCalculation}
              disabled={isCalculating}
              className="w-full"
            >
              {isCalculating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate TP/SL
                </>
              )}
            </Button>
          </div>
        )}

        {/* Results */}
        {(results.size > 0 || errors.size > 0) && (
          <div className="space-y-4">
            <h4 className="font-medium">Results</h4>
            
            {/* Success Results */}
            {results.size > 0 && (
              <div className="space-y-2">
                {Array.from(results.entries()).map(([filename, result]) => (
                  <div key={filename} className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{result.symbol}</p>
                        <p className="text-xs text-muted-foreground">
                          Price: ${formatPrice(result.currentPrice, result.symbol)} ({result.priceSource})
                        </p>
                        {positionSizeInfo.has(filename) && (
                          <p className="text-xs text-blue-600">
                            Position range: {formatLotSize(positionSizeInfo.get(filename)!.min)} - {formatLotSize(positionSizeInfo.get(filename)!.max)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm">
                          <span className="text-green-600">TP: {formatUSD(result.tpUSD)}</span>
                          <span className="mx-2">|</span>
                          <span className="text-red-600">SL: {formatUSD(result.slUSD)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          TV: {formatLotSize(result.tvPositionSize)} → cT: {formatLotSize(result.ctraderLotSize)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error Results */}
            {errors.size > 0 && (
              <div className="space-y-2">
                {Array.from(errors.entries()).map(([filename, error]) => (
                  <div key={filename} className="p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">{filename}</p>
                        <p className="text-xs text-destructive">{error}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};